import { useNavigate } from 'react-router-dom';
import { useApprovalStats } from '@saiens/shared/hooks';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  ArrowLeft,
  ClipboardList,
  History,
  CalendarOff,
  Clock,
  Users,
  Timer,
} from 'lucide-react';

export function ApprovalHubPage() {
  const navigate = useNavigate();
  const { data: stats, isLoading } = useApprovalStats();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-white px-4 py-3 shadow-sm">
        <button onClick={() => navigate(-1)} className="rounded-full p-1 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">簽核管理</h1>
      </div>

      <div className="space-y-4 px-4 py-4">
        {/* Pending Approvals */}
        <button
          onClick={() => navigate('/approval/pending')}
          className="flex w-full items-center justify-between rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
        >
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-red-50 p-2">
              <ClipboardList className="h-5 w-5 text-red-600" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900">待辦簽核</p>
              <p className="text-xs text-gray-500">待處理的簽核項目</p>
            </div>
          </div>
          {stats && stats.pending_count > 0 && (
            <span className="rounded-full bg-red-500 px-2.5 py-0.5 text-xs font-semibold text-white">
              {stats.pending_count}
            </span>
          )}
        </button>

        {/* Approval History */}
        <button
          onClick={() => navigate('/approval/history')}
          className="flex w-full items-center justify-between rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
        >
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-50 p-2">
              <History className="h-5 w-5 text-blue-600" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900">簽核歷史</p>
              <p className="text-xs text-gray-500">已完成的簽核記錄</p>
            </div>
          </div>
          {stats && stats.history_count > 0 && (
            <span className="text-xs text-gray-400">{stats.history_count} 筆</span>
          )}
        </button>

        {/* Manager Section */}
        {stats?.is_manager && (
          <>
            <div className="pt-2">
              <h2 className="mb-2 text-sm font-semibold text-gray-500">管理查詢</h2>
            </div>

            <button
              onClick={() => navigate('/approval/payment-delays')}
              className="flex w-full items-center justify-between rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-amber-50 p-2">
                  <Timer className="h-5 w-5 text-amber-600" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">延後付款審核</p>
                  <p className="text-xs text-gray-500">審核業務的延後付款申請</p>
                </div>
              </div>
              {stats && stats.payment_delay_pending_count > 0 && (
                <span className="rounded-full bg-amber-500 px-2.5 py-0.5 text-xs font-semibold text-white">
                  {stats.payment_delay_pending_count}
                </span>
              )}
            </button>

            <button
              onClick={() => navigate('/approval/leave-all')}
              className="flex w-full items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
            >
              <div className="rounded-lg bg-pink-50 p-2">
                <CalendarOff className="h-5 w-5 text-pink-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">所有請假紀錄</p>
                <p className="text-xs text-gray-500">查詢所有員工請假記錄</p>
              </div>
            </button>

            <button
              onClick={() => navigate('/approval/overtime-all')}
              className="flex w-full items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
            >
              <div className="rounded-lg bg-amber-50 p-2">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">所有加班紀錄</p>
                <p className="text-xs text-gray-500">查詢所有員工加班記錄</p>
              </div>
            </button>

            <button
              onClick={() => navigate('/approval/attendance-all')}
              className="flex w-full items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
            >
              <div className="rounded-lg bg-teal-50 p-2">
                <Users className="h-5 w-5 text-teal-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">所有打卡紀錄</p>
                <p className="text-xs text-gray-500">查詢所有員工打卡記錄</p>
              </div>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
